import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { endOfYear, endOfMonth, startOfMonth } from 'date-fns';
import type { ApexOptions } from 'apexcharts';
import {
  PeriodGroupByEnum,
  useEdmDocumentationsAnalyticsTrendQuery,
} from 'graphql-common';
import {
  datePickerYearFormat,
  datePickerShortDateFormat,
} from '@lib/enums/dateTime';
import { revertTransformDate } from '@lib/utils/dateTimeHelpers';
import getTrendGroupByOptions, {
  TREND_QUARTER,
  TrendGroupBy,
  TrendGroupByOption,
  toBackendPeriodGroupBy,
} from './utils/getTrendGroupByOptions';
import getTrendPeriod, {
  TrendViewport,
  getBucketStart,
  getBucketEnd,
  getBucketStep,
} from './utils/getTrendPeriod';
import fillTrendGaps from './utils/fillTrendGaps';
import buildTrendChartData from './utils/buildTrendChartData';

interface UseTrendNavigationArgs {
  /** [startDate, endDate] ISO strings from the page-level period filter. */
  period: string[];
}

export interface TrendNavigationResult {
  /** Currently selected grouping granularity. */
  groupBy: TrendGroupBy;
  /** Available options for the granularity select. */
  groupByOptions: TrendGroupByOption[];
  selectedGroupByOption: TrendGroupByOption | undefined;
  onGroupByChange: (selected: unknown) => void;
  /** Start of the anchor bucket shown in the DatePicker. */
  pickerBucketStart: Date;
  /** Picker variant: 'year' for Month/Quarter/Year, 'month' for Day/Week. */
  datePickerType: 'year' | 'month' | undefined;
  datePickerFormat: string;
  /** Earliest date selectable in the picker (derived from period start). */
  minBucketStart: Date | undefined;
  /** Latest date selectable in the picker (derived from period end). */
  maxBucketEnd: Date | undefined;
  /** True for Year mode — navigation hidden, picker hidden. */
  isYearLockedView: boolean;
  isDecrementDisabled: boolean;
  isIncrementDisabled: boolean;
  decrement: () => void;
  increment: () => void;
  onPickerChange: (v: string) => void;
  /** Date range sent to the API and rendered on the x-axis. */
  viewport: TrendViewport;
  /** True when at least one bucket carries non-zero view data. */
  hasAnyData: boolean;
  isLoading: boolean;
  options: ApexOptions;
  series: ApexOptions['series'];
  /** Stable Chart key — forces ApexCharts to remount on viewport/groupBy change. */
  chartKey: string;
}

/**
 * Encapsulates all state, effects, and event handlers for the
 * DocumentsViewingTrend chart, leaving the component as pure JSX.
 *
 * Navigation anchor strategy per granularity:
 *   Day / Week   → month-anchor: endDate = end-of-month, navigate by month
 *   Month / Quarter → year-anchor: endDate = end-of-year, navigate by year
 *   Year         → locked: viewport = full period range, no navigation
 */
export default function useTrendNavigation({
  period,
}: UseTrendNavigationArgs): TrendNavigationResult {
  const { t, i18n } = useTranslation();
  const groupByOptions = useMemo(() => getTrendGroupByOptions(t), [t]);

  const [groupBy, setGroupBy] = useState<TrendGroupBy>(PeriodGroupByEnum.Week);
  const initialEndIso = period?.[1] || new Date().toISOString();
  const [endDate, setEndDate] = useState<Date>(() =>
    revertTransformDate(initialEndIso),
  );

  // Parse the page-level period ISO strings into Date objects once.
  // Use revertTransformDate (CET-zoned) so endOfMonth/endOfYear computations
  // don't shift across month/year boundaries due to UTC offset rounding.
  const periodStartObj = useMemo(() => {
    if (!period?.[0]) return undefined;
    const parsed = revertTransformDate(period[0]);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }, [period]);

  const periodEndObj = useMemo(() => {
    if (!period?.[1]) return undefined;
    const parsed = revertTransformDate(period[1]);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }, [period]);

  const isMonthAnchorMode =
    groupBy === PeriodGroupByEnum.Day || groupBy === PeriodGroupByEnum.Week;
  const isYearAnchorMode =
    groupBy === TREND_QUARTER || groupBy === PeriodGroupByEnum.Month;
  const isYearLockedView = groupBy === PeriodGroupByEnum.Year;
  const isYearPickerMode = isYearLockedView || isYearAnchorMode;

  // Re-anchor endDate whenever the page-level period upper bound changes.
  useEffect(() => {
    if (!periodEndObj) return;
    setEndDate(
      isYearAnchorMode || isYearLockedView
        ? endOfYear(periodEndObj)
        : endOfMonth(periodEndObj),
    );
  }, [periodEndObj, isYearAnchorMode, isYearLockedView]);

  // DatePicker min/max: month-aligned for Day/Week, bucket-aligned for others.
  const minBucketStart = useMemo(() => {
    if (!periodStartObj) return undefined;
    return isMonthAnchorMode
      ? startOfMonth(periodStartObj)
      : getBucketStart(periodStartObj, groupBy);
  }, [periodStartObj, groupBy, isMonthAnchorMode]);

  const maxBucketEnd = useMemo(() => {
    if (!periodEndObj) return undefined;
    return isMonthAnchorMode
      ? endOfMonth(periodEndObj)
      : getBucketEnd(periodEndObj, groupBy);
  }, [periodEndObj, groupBy, isMonthAnchorMode]);

  // Year mode passes periodStartOverride so the viewport spans the entire period.
  const viewport = useMemo(
    () =>
      getTrendPeriod(
        endDate,
        groupBy,
        isYearLockedView ? periodStartObj : undefined,
      ),
    [endDate, groupBy, isYearLockedView, periodStartObj],
  );

  // The picker shows the start of the current anchor bucket (month or year).
  const pickerBucketStart = useMemo(() => {
    if (isMonthAnchorMode) return startOfMonth(endDate);
    return getBucketStart(endDate, groupBy);
  }, [endDate, groupBy, isMonthAnchorMode]);

  // Stepper disable: year-anchor compares full years; month-anchor compares months.
  const periodStartYear = periodStartObj?.getFullYear();
  const periodEndYear = periodEndObj?.getFullYear();
  const endDateYear = endDate.getFullYear();

  const isDecrementDisabled = isYearAnchorMode
    ? periodStartYear !== undefined && endDateYear <= periodStartYear
    : !!periodStartObj &&
      startOfMonth(endDate).getTime() <= startOfMonth(periodStartObj).getTime();

  const isIncrementDisabled = isYearAnchorMode
    ? periodEndYear !== undefined && endDateYear >= periodEndYear
    : !!periodEndObj &&
      startOfMonth(endDate).getTime() >= startOfMonth(periodEndObj).getTime();

  const trendQuery = useEdmDocumentationsAnalyticsTrendQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      period: { startDate: viewport.startDate, endDate: viewport.endDate },
      // BE only supports DAY/WEEK/MONTH/YEAR — QUARTER fetches MONTH and is
      // aggregated into quarter buckets by fillTrendGaps on the FE.
      periodGroupBy: toBackendPeriodGroupBy(groupBy),
      // BE caps trend rows at 100; without `limit` it defaults to 10 which would
      // clip multi-month ranges. 100 covers ~3y monthly / ~2y weekly / ~3mo daily.
      limit: 100,
    },
    skip: !viewport.startDate || !viewport.endDate,
  });

  const points = _get(trendQuery, ['data', 'data'], []) as Array<{
    period: string;
    allViews: number;
    uniqueViews: number;
  }>;

  const buckets = useMemo(
    () => fillTrendGaps(points, viewport.startObj, viewport.endObj, groupBy),
    [points, viewport.startObj, viewport.endObj, groupBy],
  );

  const hasAnyData = useMemo(
    () => buckets.some((b) => b.allViews > 0 || b.uniqueViews > 0),
    [buckets],
  );

  const { options, series } = useMemo(
    () => buildTrendChartData({ buckets, groupBy, locale: i18n.language, t }),
    [buckets, groupBy, i18n.language, t],
  );

  let datePickerType: 'year' | 'month' | undefined;
  if (isYearPickerMode) datePickerType = 'year';
  else if (isMonthAnchorMode) datePickerType = 'month';

  const datePickerFormat = isYearPickerMode
    ? datePickerYearFormat
    : datePickerShortDateFormat;

  const decrement = () => {
    if (isDecrementDisabled) return;
    setEndDate((prev) => {
      if (isYearAnchorMode)
        return endOfYear(getBucketStep(prev, PeriodGroupByEnum.Year, -1));
      return endOfMonth(getBucketStep(prev, PeriodGroupByEnum.Month, -1));
    });
  };

  const increment = () => {
    if (isIncrementDisabled) return;
    setEndDate((prev) => {
      if (isYearAnchorMode)
        return endOfYear(getBucketStep(prev, PeriodGroupByEnum.Year, 1));
      return endOfMonth(getBucketStep(prev, PeriodGroupByEnum.Month, 1));
    });
  };

  const onPickerChange = (v: string) => {
    if (!v) return;
    const parsed = revertTransformDate(v);
    if (Number.isNaN(parsed.getTime())) return;
    setEndDate(isYearAnchorMode ? endOfYear(parsed) : endOfMonth(parsed));
  };

  const onGroupByChange = (selected: unknown) => {
    const next = _get(selected, 'value') as TrendGroupBy | undefined;
    if (!next) return;
    setGroupBy(next);
    if (!periodEndObj) return;
    // Year-like anchor (Month / Quarter / Year) snaps to end-of-year;
    // month-anchor (Day / Week) snaps to end-of-month.
    const nextIsYearLike =
      next === TREND_QUARTER ||
      next === PeriodGroupByEnum.Month ||
      next === PeriodGroupByEnum.Year;
    setEndDate(
      nextIsYearLike ? endOfYear(periodEndObj) : endOfMonth(periodEndObj),
    );
  };

  const selectedGroupByOption = groupByOptions.find((o) => o.value === groupBy);

  return {
    groupBy,
    groupByOptions,
    selectedGroupByOption,
    onGroupByChange,
    pickerBucketStart,
    datePickerType,
    datePickerFormat,
    minBucketStart,
    maxBucketEnd,
    isYearLockedView,
    isDecrementDisabled,
    isIncrementDisabled,
    decrement,
    increment,
    onPickerChange,
    viewport,
    hasAnyData,
    isLoading: trendQuery.loading,
    options,
    series,
    chartKey: `${groupBy}-${viewport.startDate}-${viewport.endDate}`,
  };
}
